import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Maximize2,
  Minimize2,
  RefreshCw,
  AlertCircle,
  Wifi,
  WifiOff,
} from 'lucide-react';

import { eventReportApi } from './eventReportApi';
import { useEventMonitorSocket } from './useEventMonitorSocket';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';

/**
 * Caso 10 — Live monitor for an event in flight.
 *
 * v1 reuses `/xk-event/:id/report` with a 5-second `refetchInterval` (polling)
 * — the WebSocket gateway + sticky-sessions piece is gated on the infra
 * checklist (`docs-pend/CHECKLIST-INFRA.md`). Switching to push later only
 * requires changing the queryFn; the UI stays.
 *
 * Mode: fullscreen-friendly, big tipo, alto contraste. Designed to be
 * shown on a TV at the event venue.
 */
export function LiveMonitorPage() {
  const { id } = useParams<{ id: string }>();
  const eventId = Number(id);
  const navigate = useNavigate();
  const t = useT();
  const [fullscreen, setFullscreen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Caso 10 v2 — `queryKey` is the canonical handle for the live monitor
  // data; the socket hook invalidates it on every `monitor:tick`. We keep
  // a 5s polling fallback in case the WebSocket connect fails (sticky
  // sessions still pending per CHECKLIST-INFRA.md).
  const queryKey = useMemo(() => ['liveMonitor', eventId], [eventId]);
  const report = useQuery({
    queryKey,
    queryFn: () => eventReportApi.internal(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
    refetchInterval: 5_000,
  });

  const { connected } = useEventMonitorSocket(eventId, queryKey);

  // Track Fullscreen API state so the button label can swap.
  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    } else {
      rootRef.current?.requestFullscreen?.().catch(() => undefined);
    }
  }

  if (!Number.isFinite(eventId) || eventId <= 0) {
    return <div className="page">{t('liveMonitor.invalidId')}</div>;
  }

  return (
    <div
      ref={rootRef}
      className="min-h-screen bg-[var(--color-xk-bg,#000)] text-white"
    >
      {/* Header — hidden in fullscreen so the TV view is just the data. */}
      {!fullscreen && (
        <header className="flex items-center justify-between gap-3 border-b border-white/10 px-6 py-4">
          <button
            type="button"
            onClick={() => navigate(`/events/${eventId}/report`)}
            className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100"
          >
            <ArrowLeft className="h-4 w-4" /> {t('liveMonitor.back')}
          </button>
          <div className="flex items-center gap-2">
            {/* Caso 10 v2 — transport indicator: green when the WS is up
                (push), neutral when only polling is keeping the view
                warm. */}
            <Badge tone={connected ? 'success' : 'neutral'}>
              {connected ? (
                <Wifi className="mr-1 inline h-3 w-3" />
              ) : (
                <WifiOff className="mr-1 inline h-3 w-3" />
              )}
              {connected
                ? t('liveMonitor.transport.live')
                : t('liveMonitor.transport.polling')}
            </Badge>
            <Badge tone={report.isFetching ? 'accent' : 'neutral'}>
              <RefreshCw
                className={
                  'mr-1 inline h-3 w-3 ' +
                  (report.isFetching ? 'animate-spin' : '')
                }
              />
              {report.isFetching
                ? t('liveMonitor.refreshing')
                : t('liveMonitor.live')}
            </Badge>
            <Button variant="ghost" onClick={toggleFullscreen}>
              {fullscreen ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </header>
      )}

      <main className="px-6 py-10 lg:px-16 lg:py-16">
        {report.isLoading || !report.data ? (
          <Skeleton className="h-40" />
        ) : (
          <>
            <div className="mb-8">
              <Badge tone="accent">
                {report.data.event.name ?? t('liveMonitor.unknown')}
              </Badge>
              <h1 className="mt-2 text-4xl font-bold lg:text-6xl">
                {report.data.branding?.campaignName ?? report.data.event.name}
              </h1>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <BigKpi
                label={t('liveMonitor.kpi.interactions')}
                value={report.data.reach.interactionsTotal}
              />
              <BigKpi
                label={t('liveMonitor.kpi.last24h')}
                value={report.data.reach.interactionsLast24h}
                accent
              />
              <BigKpi
                label={t('liveMonitor.kpi.leads')}
                value={report.data.reach.leadsCaptured}
              />
              <BigKpi
                label={t('liveMonitor.kpi.prizesDistributed')}
                value={report.data.prizes.totalDistributed}
              />
            </div>

            {report.data.prizes.totalRemaining <= 5 && (
              <div className="mt-10 flex items-center gap-3 rounded-lg bg-[var(--color-warn-soft,rgba(251,191,36,0.15))] px-4 py-3 text-sm">
                <AlertCircle className="h-5 w-5 text-[var(--color-warn,#FBBF24)]" />
                <span>
                  {t('liveMonitor.alert.lowStock', {
                    count: report.data.prizes.totalRemaining,
                  })}
                </span>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function BigKpi({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div
      className={
        'rounded-2xl border px-6 py-8 ' +
        (accent
          ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft,rgba(178,98,245,0.08))]'
          : 'border-white/10 bg-white/5')
      }
    >
      <div className="text-xs font-semibold uppercase tracking-wider opacity-60">
        {label}
      </div>
      <div className="mt-3 text-5xl font-extrabold tabular-nums lg:text-7xl">
        {value.toLocaleString()}
      </div>
    </div>
  );
}
